import { api, ApiError, type Me } from "./api.js";
import { createStore, useStore } from "./store.js";

/**
 * Who is signed in, and what they own.
 *
 * The session cookie is HttpOnly, so the client cannot read it and must not try
 * to guess. `/api/me` is the only source of truth: a 401 means signed out, and
 * anything else means signed in.
 */
export type SessionState =
  | { status: "loading"; me: null }
  | { status: "anonymous"; me: null }
  | { status: "signedIn"; me: Me };

const store = createStore<SessionState>({ status: "loading", me: null });

export const sessionStore = store;
export const useSession = () => useStore(store);

export async function refreshSession(): Promise<SessionState> {
  try {
    const me = await api.me();
    store.set({ status: "signedIn", me });
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) {
      store.set({ status: "anonymous", me: null });
    } else {
      // A network blip should not log the player out of the UI; keep whatever
      // was last known and let the next refresh settle it.
      if (store.get().status === "loading") store.set({ status: "anonymous", me: null });
    }
  }
  return store.get();
}

/** Fold a partial update into the cached profile without a round trip. */
export function patchSession(patch: Partial<Me>): void {
  const current = store.get();
  if (current.status !== "signedIn") return;
  store.set({ status: "signedIn", me: { ...current.me, ...patch } });
}

export async function signOut(): Promise<void> {
  try {
    await api.logout();
  } finally {
    store.set({ status: "anonymous", me: null });
  }
}
